import React from 'react';
import { Button } from './ui/button';
import { Calendar, Loader2, Users } from 'lucide-react';
import { OnboardingSessionCard } from './onboarding/OnboardingSessionCard';
import CoViewingViewer from './onboarding/CoViewingViewer';
import { useAssistedOnboarding } from './hooks/useAssistedOnboarding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pending_review', label: 'Pending' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Done' },
];

const TYPE_FILTERS = [
  { key: 'all', label: 'All Types' },
  { key: 'quick_setup', label: 'Quick Setup' },
  { key: 'deep_onboarding', label: 'Deep Onboarding' },
];

const SessionsContent = ({ loading, sessions, handleAction, setActiveViewSession }) => {
  if (loading) {
    return <div className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading...</div>;
  }
  if (sessions.length === 0) {
    return <div className="text-center py-12 text-slate-400"><Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" /><p className="text-sm">No sessions found</p></div>;
  }
  return (
    <div className="space-y-3">
      {sessions.map(session => (
        <OnboardingSessionCard key={session.session_id} session={session} onAction={handleAction} onView={setActiveViewSession} />
      ))}
    </div>
  );
};

export default function AdminAssistedOnboarding({ adminToken }) {
  const {
    sessions, loading, filter, setFilter,
    typeFilter, setTypeFilter,
    activeViewSession, setActiveViewSession,
    loadSessions, handleAction,
  } = useAssistedOnboarding(adminToken);

  return (
    <div className="space-y-4" data-testid="admin-assisted-onboarding">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2 text-white">
            <Users className="w-5 h-5 text-emerald-400" />
            Virtual Assisted Onboarding
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">Manage Quick Virtual Setup Sessions and Deep Onboarding sessions</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadSessions} className="text-white border-slate-600 hover:bg-slate-700">
          <Loader2 className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map(f => (
          <Button key={f.key} variant={filter === f.key ? 'default' : 'outline-solid'} size="sm"
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-white border-slate-600 hover:bg-slate-700'}>
            {f.label}
          </Button>
        ))}
        <span className="text-slate-600 self-center mx-1">|</span>
        {TYPE_FILTERS.map(f => (
          <Button key={f.key} variant={typeFilter === f.key ? 'default' : 'outline-solid'} size="sm"
            onClick={() => setTypeFilter(f.key)}
            className={typeFilter === f.key ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'text-white border-slate-600 hover:bg-slate-700'}>
            {f.label}
          </Button>
        ))}
      </div>

      <SessionsContent loading={loading} sessions={sessions} handleAction={handleAction} setActiveViewSession={setActiveViewSession} />

      {activeViewSession && (
        <CoViewingViewer session={activeViewSession} adminToken={adminToken} onClose={() => setActiveViewSession(null)} />
      )}
    </div>
  );
}
